import React from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useTaxRates from '../../hooks/useTaxRates';

/**
 * Admin page to manage default tax rate
 * @returns {JSX.Element} The TaxRatesPage component
 */
const TaxRatesPage = () => {
  const { t } = useTranslation('misc');
  const { taxRate, isLoading, saving, formData, errors, handleInputChange, handleSave } =
    useTaxRates();

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
        >
          {t('tax.title')}
        </Typography>

        <Paper sx={{ mt: 3, p: 3 }}>
          <Alert
            severity="info"
            sx={{ mb: 3 }}
          >
            {t('tax.singleTaxInfo')}
          </Alert>

          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                name="name"
                label={t('tax.fields.name')}
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                name="rate"
                label={t('tax.fields.rate')}
                value={formData.rate}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                type="number"
                InputProps={{
                  inputProps: { min: 0, max: 1, step: 0.0001 },
                  endAdornment: '%',
                }}
                error={!!errors.rate}
                helperText={errors.rate || t('tax.fields.rateHelp')}
                required
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              <TextField
                name="description"
                label={t('tax.fields.description')}
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    name="isActive"
                    color="primary"
                  />
                }
                label={t('tax.fields.active')}
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  onClick={handleSave}
                  color="primary"
                  variant="contained"
                  disabled={saving}
                  startIcon={saving && <CircularProgress size={20} />}
                >
                  {t('common.save')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default TaxRatesPage;
